// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Most home contents policies cover jewelry only up to a low single-item limit, often a few hundred to a couple of thousand in local currency, and only inside the house. An antique ring worn every day is frequently the least insured valuable thing a person owns.

Fixing that is not complicated, but it needs the right kind of valuation and the right kind of cover.

## Why standard contents cover usually is not enough

Three limits catch people out.

**The single article limit.** Contents policies cap what they will pay for any one item. Anything above that has to be named on the policy separately, and insurers usually want a valuation to agree it.

**The total valuables limit.** There is often a second cap on all jewelry combined.

**Away from home.** Standard contents cover frequently stops at the front door, or covers away-from-home loss only as a paid extra. Since jewelry gets lost while being worn, that is the cover that matters.

Read your policy for those three numbers before you do anything else. If the piece is worth more than the single article limit, it is not properly covered.

## The valuation you need is not the one you want for selling

This is the distinction that confuses everyone, and it is worth being precise about.

**Insurance replacement value** is what it would cost to replace the piece with an equivalent, buying at retail, today. It is the highest of all valuation figures.

**Fair market value** is what a buyer would actually pay you for it. It is much lower, and it is the number you use when selling. Our [resale value guide](/blog/how-much-is-my-jewelry-worth) explains why the two differ by so much.

For insurance you want replacement value. Asking for the wrong one is the most common mistake: people get a fair market valuation, insure at that figure, and find they cannot replace the piece after a loss.

For antique pieces there is a further wrinkle. A genuinely irreplaceable piece cannot be replaced at any price, so a good valuation for an antique states what it would cost to source a comparable piece on the antique market, not what it would cost to have a copy made.

## Getting a valuation

You want a written valuation from someone independent, with credentials, who does not also want to buy the piece.

It should contain:

- A full description: metal, purity, weights, measurements, stone dimensions
- Photographs
- Any marks, transcribed
- The basis of valuation, stated as replacement value
- The date and the valuer's qualifications

Expect to pay a fee, usually a fixed amount per item rather than a percentage of value. Be wary of anyone charging a percentage, because it gives them a reason to inflate the figure.

**Revalue every few years.** Gold prices move, and a valuation from 2015 will not replace anything in today's market. Many insurers require a valuation no more than three to five years old.

## What to do before you insure anything

Do this whether or not you ever make a claim. It takes an evening.

1. **Photograph every piece** from several angles, in daylight, including the hallmark and any identifying feature. Our [phone photography guide](/blog/how-to-photograph-jewelry-with-your-phone) covers how.
2. **Weigh each piece** and write it down.
3. **Record the measurements**, including ring sizes and chain lengths.
4. **Keep receipts**, and any laboratory reports.
5. **Note the distinguishing marks**: a specific inclusion in a stone, an unusual claw, a repair, an inscription.
6. **Store the record somewhere that is not your house.** A cloud folder or an email to yourself.

That last point matters. A record that burns with the house proves nothing.

Those identifying details are also what lets a piece be recovered if it is stolen and later surfaces, and they are what proves a returned item is not the one you sent if you ever sell.

## Buying second hand: insure it from day one

A piece bought second hand needs the same treatment as one bought new, and the paperwork is usually thinner.

- Get the seller's description and photographs saved before the listing disappears.
- Keep the payment record. It evidences what you paid, which matters for an insurer even if replacement value is higher.
- Get it valued once it arrives, if it is worth more than your single article limit.
- Photograph it yourself, in your possession, in daylight.

Do not assume a seller's valuation transfers to you. Insurers generally want a valuation in the current owner's name.

## Cover for the piece while it is in transit

A piece being posted to you, or being sent back, is a specific gap.

- The **carrier's insurance** covers up to the declared value only, and several carriers exclude or cap jewelry entirely regardless of what you declared. Read the exclusions before choosing the service.
- **Under-declaring voids it**, which is one of several reasons not to. Our [customs guide](/blog/customs-and-import-duties-on-jewelry) covers the rest.
- **Signature on delivery** is worth the small extra on anything valuable. Tracking proves a parcel reached an address; a signature proves a person took it.

On a marketplace this should not be optional. Every sale on Nextmine must ship insured and tracked, with the tracking number entered on the order page, and if an uninsured parcel is lost the loss falls on the seller who chose not to insure it.

## What to tell your insurer

Be straightforward. Say the piece is antique, give its age, and give the valuation. Some insurers price antique and irreplaceable items differently, and a few will want the valuation to specify that a comparable piece would have to be sourced rather than made.

If you wear the piece daily, say so. Cover that assumes a ring lives in a box is cover that may be argued about after a loss.

## The short version

- Check your single article limit before anything else.
- Get a written **replacement value** valuation, not a fair market one.
- Photograph, weigh and measure everything, and store the record off-site.
- Revalue every few years.
- Insure in transit properly, and never under-declare.

If you are buying, our [guide to buying an antique engagement ring](/blog/how-to-buy-an-antique-engagement-ring) covers the questions to ask before the money moves, and [how payments work](/how-payments-work) explains the protection on the purchase itself.
`;

export default body;
